import { abnOptionMapping } from "constants/abnNotice";
import { NOT_SCHEDULED_DATE, orderTypeConstants } from "constants/order";
import { payerTypeConstants } from "constants/payer";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import dayjs from "dayjs";
import {
  getAbnProcedureLines,
  getAbnSelectionProcedures,
  getNonCoveredProcedures,
} from "helpers/abn";
import { parseDate, parseTime } from "helpers/date";
import {
  DYNAMIC_DESCRIPTION_TOKEN,
  getDynamicEstimateDescription,
} from "helpers/estimateDescription";
import { interpolateString } from "helpers/interpolate";
import {
  resolveOrderBenefits,
  resolveOrderSecondaryBenefits,
} from "helpers/orderBenefits";
import { parsePrice, centsToDollars } from "helpers/price";
import {
  getAccountLevelPaymentPlanAvailabilityEnabled,
  getPaymentPlanEnabled,
} from "pages/CreateOrder/utils/helpers";
import { disclaimerDefaultValues } from "pages/Customize/helper";
import { getAbnSelectionOptions } from "pages/OrderOverview/components/Overview/components/AbnSelection/abnDocument";
import { medicareNote } from "pages/OrderOverview/components/Overview/components/AbnSelection/constants";
import { Button } from "primereact/button";
import { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useCreateAbnNoticeMutation } from "store/queries/abnNotices";
import { authSelector } from "store/slices/auth";
import { TourAnchor } from "training/anchors";
import {
  ICreateAbnNoticeRequest,
  AbnTemplateType,
  AbnOption,
  IDocumentHashDataDto,
} from "types/AbnNotice/AbnNotice";
import { SpecialtyList } from "types/Provider/Provider";

import { getAbnReason } from "../helpers";
import {
  HorizontalLine,
  StyledSectionContainer,
  StyledSelectButton,
  StyledPriceContainer,
  StyledTag,
} from "../styled";
import AbnSelection from "./AbnSelection/AbnSelection";
import { AwaitingProviderReview } from "./AwaitingProviderReview.tsx/AwaitingProviderReview";
import BreakdownTable from "./BreakdownTable";
import DetailItem from "./DetailItem";
import DisclaimerDialog from "./DisclaimerDialog";
import InsuranceSection from "./InsuranceSection";
import NonCovered from "./NonCovered";
import PaymentPlan from "./PaymentPlan";
import { ServiceInfo } from "./ServiceInfo";

export function Home({
  content,
  orderInfo,
  procedures,
  isGFE,
  showClearEstimateDetails,
  showClearEstimateDetailsSection,
  setDisclaimerDialogVisible,
  facilityFee,
  physicianFee,
  clearPrice,
  fullPrice,
  percentageOff,
  totalSaving,
  totalAllowedInsuranceAmount,
  totalInsurancePayInCents,
  totalFlatCopayInCents,
  totalDeductibleInCents,
  totalCoinsuranceInCents,
  expirationDays,
  setShowPaymentSection,
  publicContact,
  disclaimerDialogVisible,
  refetchOrder,
  isInternalUser,
}: any) {
  const [createAbnNoticeMutation, { isLoading: isCreatingAbnNotice }] =
    useCreateAbnNoticeMutation();
  const [showPaymentsSection, setShowPaymentsSection] = useState(false);
  const [payInFull, setPayInFull] = useState(true);
  const [selectedAbnOption, setSelectedAbnOption] = useState<string>("");
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const { current: toastElement } = useContext(ToastContext);
  const { token } = useSelector(authSelector);
  const { userInfo } = useUserWithAccount();
  const isAuthenticated = !!token && !!userInfo;

  const parseText = (text: string) => {
    return interpolateString(text, {
      actualSavings: parsePrice(totalSaving),
      [DYNAMIC_DESCRIPTION_TOKEN]: getDynamicEstimateDescription(
        orderInfo?.serviceDetails?.procedures
      ),
    });
  };

  const hasActivePaymentPlan = useMemo(() => {
    return !!orderInfo.paymentPlan?.find((p: any) => p.active);
  }, [orderInfo]);

  const showPaymentPlanSection = useMemo(() => {
    const paymentPlanIsEnabledForAccount =
      getAccountLevelPaymentPlanAvailabilityEnabled(orderInfo);
    const paymentPlanIsEnabledForOrderType = getPaymentPlanEnabled(
      orderInfo.orderType,
      orderInfo.account.paymentPlanTypeEnabled
    );
    const { partialPaymentsEnabled } = orderInfo.account;

    // Show for authenticated users even if no active payment plan (so they can create one)
    if (isAuthenticated) {
      if (hasActivePaymentPlan) {
        return true;
      }
      return paymentPlanIsEnabledForOrderType || partialPaymentsEnabled;
    }

    // For public users, only show if there's an active payment plan
    const isDownpaymentOnlyPaymentPlan = orderInfo.paymentPlan?.find(
      (p: any) => p.active
    )?.isDownPaymentOnly;
    return isDownpaymentOnlyPaymentPlan
      ? partialPaymentsEnabled
      : hasActivePaymentPlan && paymentPlanIsEnabledForAccount;
  }, [orderInfo, isAuthenticated, hasActivePaymentPlan]);

  const isPatientResponsibility = useMemo(() => {
    return orderInfo.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;
  }, [orderInfo]);

  const fullProviderBreakdown = useMemo(() => {
    return orderInfo?.paymentFullBreakdown?.bundledProviderAmounts;
  }, [orderInfo]);

  const exparel = useMemo(() => {
    const exparelProceduresAmount = orderInfo.serviceDetails.procedures
      .filter((p: any) => p?.code === "Exparel")
      .reduce((acc: number, current: any) => {
        return acc + (current?.amountInCents || 0);
      }, 0);
    return exparelProceduresAmount || 0;
  }, [orderInfo]);

  const scd = useMemo(() => {
    const scdProceduresAmount = orderInfo.serviceDetails.procedures
      .filter((p: any) => p?.code === "SCD")
      .reduce((acc: number, current: any) => {
        return acc + (current?.amountInCents || 0);
      }, 0);
    return scdProceduresAmount || 0;
  }, [orderInfo]);

  const facilityFees = useMemo(() => {
    const sumOfFacilityFees = fullProviderBreakdown
      ?.filter((p: any) => p?.specialty === SpecialtyList.FACILITY)
      .reduce((acc: number, current: any) => {
        return acc + (current?.clearDiscountedPrice || 0);
      }, 0);
    return sumOfFacilityFees - scd - exparel;
  }, [fullProviderBreakdown, scd, exparel]);

  const surgeonFees = useMemo(() => {
    const sumOfSurgeonFees = fullProviderBreakdown
      ?.filter((p: any) => p?.specialty === SpecialtyList.PRIMARY_PROVIDER)
      .reduce((acc: number, current: any) => {
        return acc + (current?.clearDiscountedPrice || 0);
      }, 0);
    return sumOfSurgeonFees || 0;
  }, [fullProviderBreakdown]);

  const anesthesiaFees = useMemo(() => {
    const sumOfAnesthesiaFees = fullProviderBreakdown
      ?.filter((p: any) => p?.specialty === SpecialtyList.ANESTHESIOLOGIST)
      .reduce((acc: number, current: any) => {
        return acc + (current?.clearDiscountedPrice || 0);
      }, 0);
    return sumOfAnesthesiaFees || 0;
  }, [fullProviderBreakdown]);

  const insuranceBenefits = useMemo(() => {
    if (!orderInfo || orderInfo.orderType !== orderTypeConstants.GFE)
      return null;
    // CLE-2635 — resolve through the eligibility archive when there is no quote.
    const benefits = resolveOrderBenefits(orderInfo);
    const secondary = resolveOrderSecondaryBenefits(orderInfo);
    const primaryInsuranceDetails = {
      name: orderInfo?.payer?.name,
      deductible: benefits.remainingDeductibleInCents,
      outOfPocketMax: benefits.outOfPocketMaxInCents,
      flatCopay: benefits.flatCopayInCents,
      coinsurancePercent: benefits.coinsurancePercent,
      benefitsUpdatedAt: benefits.benefitsUpdatedAt,
    };
    const secondaryInsuranceDetails = orderInfo?.secondaryPayer
      ? {
          name: orderInfo?.secondaryPayer?.name,
          deductible: secondary.remainingDeductibleInCents,
          outOfPocketMax: secondary.outOfPocketMaxInCents,
          flatCopay: secondary.flatCopayInCents,
          coinsurancePercent: secondary.coinsurancePercent,
          benefitsUpdatedAt: secondary.benefitsUpdatedAt,
        }
      : null;
    return [primaryInsuranceDetails, secondaryInsuranceDetails].filter(
      (insurance) => insurance !== null
    );
  }, [orderInfo]);

  const nonCoveredProcedures = useMemo(() => {
    return getNonCoveredProcedures(orderInfo);
  }, [orderInfo]);

  const paymentBreakdown = useMemo(() => {
    if (!orderInfo || orderInfo.orderType !== orderTypeConstants.GFE)
      return null;
    const nonCoveredCPTCodes = nonCoveredProcedures.map((p: any) => p?.code);
    const coveredProcedures =
      orderInfo.paymentFullBreakdownByCPTCode?.bundledProviderAmounts?.filter(
        (p: any) => !nonCoveredCPTCodes.includes(p?.cptCode)
      );

    const grouped: Record<
      string,
      {
        specialty: string;
        insurance_rate: number;
        insurance_pays: number;
        you_pay: number;
      }
    > = {};

    coveredProcedures.forEach((item: any) => {
      const key = item?.specialty || "UNKNOWN";
      const amountInCents = item?.adjustedAllowablePrice || 0;
      const owedByPatientTotal = item?.patientPortion || 0;

      if (!grouped[key]) {
        grouped[key] = {
          specialty: key,
          insurance_rate: 0,
          insurance_pays: 0,
          you_pay: 0,
        };
      }

      grouped[key].insurance_rate += amountInCents;
      grouped[key].you_pay += owedByPatientTotal;
    });

    return Object.values(grouped).map((entry) => ({
      ...entry,
      insurance_pays: entry.insurance_rate - entry.you_pay,
    }));
  }, [orderInfo, nonCoveredProcedures]);

  const clearEstimateDetailsInfo = useMemo(() => {
    if (!isGFE) return [];
    return [
      {
        title: "Total insurance allowable",
        value: parsePrice(centsToDollars(totalAllowedInsuranceAmount)),
      },
      {
        title: "Insurance will Pay",
        value: parsePrice(centsToDollars(totalInsurancePayInCents)),
      },
      {
        title: "Flat Copay Amount Applied",
        value: parsePrice(centsToDollars(totalFlatCopayInCents)),
      },
      {
        title: "Deductible Amount Applied",
        value: parsePrice(centsToDollars(totalDeductibleInCents)),
      },
      {
        title: "Co-insurance Amount Applied",
        value: parsePrice(centsToDollars(totalCoinsuranceInCents)),
      },
    ];
  }, [
    totalAllowedInsuranceAmount,
    totalInsurancePayInCents,
    totalFlatCopayInCents,
    totalDeductibleInCents,
    totalCoinsuranceInCents,
    isGFE,
  ]);

  const facilityFeesDetailsInfo = useMemo(() => {
    if (!isGFE) return [];
    const exparelValue = {
      title: "Exparel",
      value: parsePrice(centsToDollars(exparel)),
    };
    const scdValue = {
      title: "SCD",
      value: parsePrice(centsToDollars(scd)),
    };

    const surgeon = {
      title: "Surgeon fees",
      value: parsePrice(centsToDollars(surgeonFees)),
    };

    const anesthesia = {
      title: "Anesthesia fees",
      value: parsePrice(centsToDollars(anesthesiaFees)),
    };
    return [
      ...(surgeonFees ? [surgeon] : []),
      ...(anesthesiaFees ? [anesthesia] : []),
      {
        title: "Facility fees",
        value: parsePrice(centsToDollars(facilityFees)),
      },
      ...(exparel ? [exparelValue] : []),
      ...(scd ? [scdValue] : []),
    ];
  }, [surgeonFees, anesthesiaFees, facilityFees, exparel, scd]);

  const patientResponsibilityFeesInfo = useMemo(() => {
    if (orderInfo.orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY)
      return [];
    const totalInsuranceAllowable =
      orderInfo?.serviceDetails?.totalInsuranceAllowableInCents;
    const insuranceWillPay = totalInsuranceAllowable - orderInfo.amountInCents;
    return [
      {
        title: "Total Insurance Allowable",
        value: parsePrice(centsToDollars(totalInsuranceAllowable)),
      },
      {
        title: "Insurance will Pay",
        value: parsePrice(centsToDollars(insuranceWillPay)),
      },
      {
        title: "Deductible + Co-insurance Amount Applied",
        value: parsePrice(centsToDollars(orderInfo.amountInCents)),
      },
    ];
  }, [orderInfo]);

  const isSelfPayRate = useMemo(() => {
    if (!orderInfo || orderInfo.orderType !== orderTypeConstants.GFE)
      return false;
    return orderInfo.payer.name === "Self-Pay Rate";
  }, [orderInfo]);

  const isPRWithTotalInsuranceAllowable = useMemo(() => {
    const isPatientResponsibility =
      orderInfo.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;
    const isTotalInsuranceAllowable =
      orderInfo?.serviceDetails?.totalInsuranceAllowableInCents > 0;
    return isPatientResponsibility && isTotalInsuranceAllowable;
  }, [orderInfo]);

  const abnSelectionProcedures = useMemo(() => {
    return getAbnSelectionProcedures(orderInfo);
  }, [orderInfo]);

  // Display only: each ABN code with its per-specialty lines for the checkout list.
  const abnDisplayProcedures = useMemo(() => {
    return abnSelectionProcedures?.map((procedure) => ({
      ...procedure,
      lines: getAbnProcedureLines(orderInfo, procedure.cptCode),
    }));
  }, [abnSelectionProcedures, orderInfo]);

  const abnSelectionTotalAmount = useMemo(() => {
    if (!abnSelectionProcedures) return 0;
    return abnSelectionProcedures.reduce(
      (total: number, procedure: any) => total + (procedure.amount || 0),
      0
    );
  }, [abnSelectionProcedures]);

  const initialAbnNotice = useMemo(() => {
    return orderInfo?.abnNotices?.filter(
      (abnNotice: any) => !abnNotice.isVoid
    )[0];
  }, [orderInfo]);

  const initialAbnSelectionProcedures = useMemo(() => {
    return initialAbnNotice?.documentSnapshot?.procedures;
  }, [initialAbnNotice]);

  const isAbnNoticeSigned = useMemo(() => {
    // If no initial ABN notice or no procedures, ABN is not signed
    if (
      !initialAbnNotice ||
      !initialAbnSelectionProcedures?.length ||
      !abnSelectionProcedures?.length
    ) {
      return false;
    }

    // Check if the payer has changed - if so, ABN is not valid for current payer
    const currentPayerId = orderInfo?.payer?.id;
    const initialPayerId = initialAbnNotice?.payerId;
    if (currentPayerId !== initialPayerId) {
      return false;
    }

    // If lengths differ, procedures have changed
    if (
      abnSelectionProcedures.length !== initialAbnSelectionProcedures.length
    ) {
      return false;
    }

    // Create a map of initial procedures
    const initialProceduresMap = new Map(
      initialAbnSelectionProcedures.map((proc: any) => [
        proc.cptCode,
        { description: proc.cptDescription, amount: proc.amount },
      ])
    ) as any;

    // Check that every current procedure exists in the initial set with identical values
    const allMatch = abnSelectionProcedures.every((currentProc: any) => {
      const initialProc = initialProceduresMap.get(currentProc.cptCode);
      return (
        initialProc &&
        initialProc.description === currentProc.cptDescription &&
        initialProc.amount === currentProc.amount
      );
    });

    return allMatch;
  }, [
    abnSelectionProcedures,
    initialAbnSelectionProcedures,
    initialAbnNotice,
    orderInfo,
  ]);

  const continueButtonDisabled = useMemo(() => {
    if (!hasActivePaymentPlan && !payInFull && isAuthenticated) {
      return true;
    }
    if (!isGFE) {
      return false;
    }
    return abnSelectionProcedures?.length ? !isAbnNoticeSigned : false;
  }, [
    isAbnNoticeSigned,
    abnSelectionProcedures,
    isGFE,
    payInFull,
    hasActivePaymentPlan,
    isAuthenticated,
  ]);

  // Two independent account settings can suppress the continue button: turning
  // it off outright, or hiding checkout while the visit is not scheduled yet.
  const showContinueButton = useMemo(() => {
    const features = orderInfo.account?.features;
    if (!features?.checkoutContinueButtonVisible) {
      return false;
    }
    return !(
      features.hideCheckoutWhenNotScheduled &&
      parseDate(orderInfo.dateOfService) === NOT_SCHEDULED_DATE
    );
  }, [orderInfo.account?.features, orderInfo.dateOfService]);

  const processedAbnSelectionOptions = useMemo(() => {
    return getAbnSelectionOptions(
      orderInfo?.payer?.type === payerTypeConstants.MEDICARE,
      abnSelectionTotalAmount
    );
  }, [orderInfo?.payer?.type, abnSelectionTotalAmount]);

  const options = useMemo(() => {
    return ["Pay in Full", "Payment Plan"];
  }, []);

  const showAwaitingProviderReview = useMemo(() => {
    const abnOptionSelected = initialAbnNotice?.abnOption;
    if (
      isAbnNoticeSigned &&
      [
        AbnOption.DeclineService,
        AbnOption.DeclineWithoutFinancialResponsibility,
      ].includes(abnOptionSelected)
    ) {
      return true;
    }
    return false;
  }, [isAbnNoticeSigned, initialAbnNotice]);

  const saveButtonDisabled = useMemo(() => {
    return !signatureData || !selectedAbnOption;
  }, [signatureData, selectedAbnOption]);

  const handleSaveSignature = async () => {
    if (!signatureData || !selectedAbnOption) return;
    const abnOption = abnOptionMapping[selectedAbnOption] as AbnOption;
    if (!abnOption) return;
    let templateType: AbnTemplateType = AbnTemplateType.ABN_Medicare;
    if (orderInfo?.payer?.type === payerTypeConstants.MEDICARE_ADVANTAGE) {
      templateType = AbnTemplateType.ABN_UhcMedicareAdvantage;
    }
    if (orderInfo?.payer?.type === payerTypeConstants.COMMERCIAL) {
      templateType = AbnTemplateType.ABN_Commercial;
    }
    const documentHashData = {
      availableOptions: processedAbnSelectionOptions,
      selectedOption: processedAbnSelectionOptions.find(
        (option) => option.value === selectedAbnOption
      ),
      reason: getAbnReason(
        orderInfo?.payer?.type === payerTypeConstants.MEDICARE
      ),
      ...(orderInfo?.payer?.type === payerTypeConstants.MEDICARE && {
        note: medicareNote,
      }),
    } as IDocumentHashDataDto;

    // Get IP address
    let signatureIpAddress: string | undefined;
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      signatureIpAddress = data.ip;
    } catch (error) {
      console.error("Failed to fetch IP address:", error);
    }

    const createAbnNoticeRequest: ICreateAbnNoticeRequest = {
      orderId: orderInfo.id,
      signatureImageBase64: signatureData,
      templateType,
      abnOption,
      // Saved into the ABN snapshot with the lines shown above, so the PDF prints the same.
      procedures: abnDisplayProcedures,
      documentHashData,
      signatureIpAddress,
      userAgent: navigator.userAgent,
    };
    await createAbnNoticeMutation(createAbnNoticeRequest)
      .unwrap()
      .then((response) => {
        toastElement?.show({
          severity: "success",
          summary: "Success",
          detail: "Signature saved successfully",
        });
        refetchOrder();
      })
      .catch((error) => {
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: error?.data?.message || "Failed to save signature",
        });
      });
  };

  const handleContinue = async () => {
    if (payInFull) {
      setShowPaymentSection({
        paymentPlan: false,
        paymentSection: true,
        paymentSuccess: false,
      });
    } else {
      setShowPaymentSection({
        paymentPlan: true,
        paymentSection: false,
        paymentSuccess: false,
      });
    }
  };

  const justifyTemplate = (option: any) => {
    if (option === options[1]) {
      return (
        <span
          className="p-button-label p-c"
          data-tour={TourAnchor.PaymentPlanOption}
        >
          {option}
        </span>
      );
    }
    const discountPercentTotal =
      content?.discountPercentTotal === undefined ||
      content?.discountPercentTotal === true;
    return (
      <div
        className="p-button-label p-c flex gap-2 justify-content-center align-items-center"
        data-tour={TourAnchor.PayInFullOption}
        style={{
          marginBottom: disclaimerDefaultValues && "-2px",
          marginTop: disclaimerDefaultValues && "-2px",
        }}
      >
        {option}
        {discountPercentTotal && (
          <StyledTag severity="success" value={`Save ${percentageOff}%`} />
        )}
      </div>
    );
  };

  if (showAwaitingProviderReview) {
    return <AwaitingProviderReview publicContact={publicContact} />;
  }

  return (
    <div data-tour={TourAnchor.BillingStatement}>
      <h2 className="text-base md:text-xl line-height-3 font-semibold mb-4 purple">
        {parseText(content?.headline)}
      </h2>
      <StyledSectionContainer className="px-3">
        <div className="flex flex-column w-full">
          <p className="line-height-2 font-medium text-xs">SERVICES PROVIDED</p>
          {parseDate(orderInfo.dateOfService) !== NOT_SCHEDULED_DATE && (
            <p className="text-base line-height-3 font-medium highlighted mt-2">
              {parseDate(orderInfo.dateOfService)}{" "}
              {parseTime(orderInfo.dateOfService)}
            </p>
          )}
          <ServiceInfo
            title={`${procedures[0].cptDescription}`}
            providers={
              isPatientResponsibility
                ? [{ name: orderInfo.account.name }]
                : procedures[0].providers
            }
            quantity={procedures[0].providers[0]?.quantity}
            code={procedures[0].code}
          />
          {showPaymentsSection &&
            procedures
              .slice(1)
              .map((procedure: any) => (
                <ServiceInfo
                  title={`${procedure.cptDescription}`}
                  providers={
                    isPatientResponsibility
                      ? [{ name: orderInfo.account.name }]
                      : procedure.providers
                  }
                  key={`${procedure.code}-${procedure.providers[0]?.id}`}
                  quantity={procedure.providers[0]?.quantity}
                  code={procedure.code}
                />
              ))}
        </div>
        {procedures.length > 1 && (
          <Button
            label={showPaymentsSection ? "Hide services" : "View all services"}
            className="p-button-text p-0 w-fit text-sm"
            style={{ color: "#4f46e5" }}
            icon={`pi pi-chevron-up transition-duration-500 transition-all ${
              showPaymentsSection ? "" : "rotate-180"
            }`}
            iconPos="right"
            onClick={() => setShowPaymentsSection(!showPaymentsSection)}
          />
        )}
      </StyledSectionContainer>
      <StyledSectionContainer>
        <div
          className={`flex flex-column w-full gap-3 px-3  ${
            (isGFE || isPRWithTotalInsuranceAllowable) &&
            showClearEstimateDetailsSection &&
            "shadow pb-3"
          }`}
        >
          <div className="flex flex-column w-full">
            <p className="line-height-2 font-medium text-xs mb-2">
              ACCOUNT SUMMARY
            </p>
            <div className="flex justify-content-between gap-4 sm:gap-8">
              <div className="flex flex-column">
                <p className="text-base line-height-3 font-semibold highlighted mb-1">
                  {content?.estimateTitle?.trim().length
                    ? parseText(content.estimateTitle)
                    : "Estimated Payment Due"}
                </p>
                <p className="text-sm line-height-2 font-normal">
                  {parseText(content?.estimateDescription)}
                </p>
              </div>
              <div
                className="flex-initial flex-column mb-2 w-100"
                style={{
                  minWidth: "80px",
                  marginTop: content?.facilityFeeDescription?.length
                    ? "-15px"
                    : "",
                }}
              >
                {content?.facilityEstimate && (
                  <div className="flex flex-column">
                    <p className="text-xs gray text-right">
                      {parseText(content?.facilityFeeDescription)}
                    </p>
                    <p className="text-base line-height-3 font-semibold highlighted text-right">
                      {parsePrice(facilityFee)}
                    </p>
                  </div>
                )}

                {content?.physicianEstimate && (
                  <div className="flex flex-column mt-2">
                    <p className="text-xs gray text-right">Physician fee</p>
                    <p className="text-base line-height-3 font-semibold highlighted text-right">
                      {parsePrice(physicianFee)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {(isGFE || isPRWithTotalInsuranceAllowable) && (
            <Button
              label={
                showClearEstimateDetailsSection
                  ? "Hide Details"
                  : "Why do I owe this amount?"
              }
              className="p-button-text p-0 w-fit text-sm"
              style={{ color: "#4f46e5" }}
              icon={`pi pi-chevron-up transition-duration-500 transition-all ${
                showClearEstimateDetailsSection ? "" : "rotate-180"
              }`}
              iconPos="right"
              onClick={showClearEstimateDetails}
              data-tour={TourAnchor.WhyOwe}
            />
          )}
        </div>
        {showClearEstimateDetailsSection &&
          (isGFE || isPRWithTotalInsuranceAllowable) && (
            <div className="flex flex-column w-full gap-3 px-3">
              <div className="flex flex-column w-full gap-1">
                {isGFE && (
                  <div className="flex flex-column w-full gap-1">
                    {!isSelfPayRate &&
                      insuranceBenefits?.map((insurance: any, index) => (
                        <InsuranceSection
                          key={`${insurance.name}_${index}`}
                          name={insurance.name}
                          deductible={insurance.deductible}
                          outOfPocketMax={insurance.outOfPocketMax}
                          flatCopay={insurance.flatCopay}
                          coinsurancePercent={insurance.coinsurancePercent}
                          isSecondary={index === 1}
                          benefitsUpdatedAt={insurance.benefitsUpdatedAt}
                          orderCreatedDate={orderInfo.createdAt}
                        />
                      ))}
                    {paymentBreakdown && !isSelfPayRate && (
                      <BreakdownTable breakdown={paymentBreakdown as any} />
                    )}
                    <p className="text-xs line-height-3 font-normal mb-2 px-3">
                      We’ll reallocate your payments if, after insurance
                      adjustments, your responsibility shifts from one provider
                      to another. This can happen when one provider bills
                      earlier than expected.
                    </p>
                    <HorizontalLine />
                    {nonCoveredProcedures.length > 0 && !isSelfPayRate && (
                      <>
                        <NonCovered
                          nonCoveredProcedures={nonCoveredProcedures}
                        />
                        <HorizontalLine />
                      </>
                    )}
                    {isSelfPayRate &&
                      facilityFeesDetailsInfo.map((info: any, index) => (
                        <DetailItem
                          title={info.title}
                          value={info.value}
                          key={`${info.title}_${index}`}
                        />
                      ))}
                  </div>
                )}
                {isPRWithTotalInsuranceAllowable && (
                  <div className="flex flex-column w-full gap-1">
                    {patientResponsibilityFeesInfo.map((info: any, index) => (
                      <DetailItem
                        title={info.title}
                        value={info.value}
                        key={`${info.title}_${index}`}
                      />
                    ))}
                  </div>
                )}
                <Button
                  className="p-button-text text-sm p-0 text-left"
                  style={{ color: "#4f46e5" }}
                  onClick={() => setDisclaimerDialogVisible(true)}
                >
                  {content.disclaimerTitleEnglish ||
                    disclaimerDefaultValues.disclaimerTitleEnglish}
                </Button>
                <DisclaimerDialog
                  visible={disclaimerDialogVisible}
                  onClose={() => setDisclaimerDialogVisible(false)}
                  englishTitle={
                    content.disclaimerTitleEnglish ||
                    disclaimerDefaultValues.disclaimerTitleEnglish
                  }
                  englishDescription={
                    content.disclaimerDescriptionEnglish ||
                    disclaimerDefaultValues.disclaimerDescriptionEnglish
                  }
                  spanishTitle={
                    content.disclaimerTitleSpanish ||
                    disclaimerDefaultValues.disclaimerTitleSpanish
                  }
                  spanishDescription={
                    content.disclaimerDescriptionSpanish ||
                    disclaimerDefaultValues.disclaimerDescriptionSpanish
                  }
                />
              </div>
            </div>
          )}
      </StyledSectionContainer>
      {abnSelectionProcedures && abnSelectionProcedures.length > 0 && isGFE && (
        <StyledSectionContainer>
          <AbnSelection
            procedures={abnDisplayProcedures || []}
            patient={orderInfo.patient}
            selectedAbnOption={selectedAbnOption}
            setSelectedAbnOption={setSelectedAbnOption}
            isAbnNoticeSigned={isAbnNoticeSigned}
            setSignatureData={setSignatureData}
            options={processedAbnSelectionOptions}
            isMedicare={orderInfo?.payer?.type === payerTypeConstants.MEDICARE}
            onSaveSignature={handleSaveSignature}
            saveButtonDisabled={saveButtonDisabled}
            isSavingSignature={isCreatingAbnNotice}
          />
        </StyledSectionContainer>
      )}
      {showPaymentPlanSection && (
        <StyledSelectButton
          value={payInFull ? options[0] : options[1]}
          onChange={(e) =>
            e.value === options[0] ? setPayInFull(true) : setPayInFull(false)
          }
          options={options}
          itemTemplate={justifyTemplate}
        />
      )}
      {payInFull && (
        <>
          <StyledPriceContainer data-tour={TourAnchor.PayNowPrice}>
            <div className="flex flex-column gap-1">
              <h2 className="text-base font-semibold line-height-3">
                {parseText(content?.clearPriceTitle) || "Pay Now Price"}
              </h2>
              <p className="text-sm font-normal line-height-2">
                {parseText(content?.clearPriceDescription)}
              </p>
            </div>
            <div className="flex flex-column gap-1 line-height-4 font-semibold text-right">
              <p className="purple text-xl">{parsePrice(clearPrice)}</p>
              {(content?.discountPercentTotal === undefined ||
                content?.discountPercentTotal === true) && (
                <div className="flex flex-column gap-1">
                  <p className="text-xs green font-normal line-height-1">{`${percentageOff}% off`}</p>
                  <p className="text-xs font-normal line-height-1">
                    Total price:{" "}
                    <span style={{ textDecoration: "line-through" }}>
                      {parsePrice(fullPrice)}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </StyledPriceContainer>
          {content?.discountExpires &&
            parseDate(orderInfo.dateOfService) !== NOT_SCHEDULED_DATE && (
              <p className="mt-2 text-center red text-base font-semibold line-height-3">
                Discount expires on{" "}
                {dayjs
                  .utc(orderInfo.dateOfService)
                  .add(expirationDays, "day")
                  .format("MMMM D, YYYY")}
              </p>
            )}
        </>
      )}
      {!payInFull && showPaymentPlanSection && (
        <PaymentPlan
          orderInfo={orderInfo}
          fullPrice={fullPrice}
          clearPrice={clearPrice}
          onPaymentPlanUpdate={refetchOrder}
          isAuthenticated={isAuthenticated}
        />
      )}
      {showContinueButton && (
        <Button
          className="w-full justify-content-center mt-6 border-round-lg"
          data-tour={TourAnchor.Continue}
          onClick={handleContinue}
          disabled={continueButtonDisabled}
        >
          Continue
        </Button>
      )}
      <div className="flex justify-content-center gap-4 mt-4">
        <div className="text-center gap-1 flex flex-column">
          <p className="text-sm line-height-3 font-semibold">Clear Health</p>
          <p className="text-sm line-height-3 font-normal">
            Estimate and Payment Support
          </p>
          <p className="text-sm line-height-3 font-normal">(888) 918-2522</p>
        </div>
        {publicContact().isVisible && (
          <div className="text-center gap-1 flex flex-column">
            <p className="text-sm line-height-3 font-semibold">
              {publicContact().name}
            </p>
            <p className="text-sm line-height-3 font-normal">
              {" "}
              {publicContact().role}
            </p>
            <p className="text-sm line-height-3 font-normal">
              {" "}
              {publicContact().phone}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
